import React, { useState, useEffect } from "react";
import { auth } from "../../firebase/firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
} from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { useAnalytics } from "../../firebase/hooks/useAnalytics";
import {
  authSearch,
  readRedirect,
  redirectTarget,
} from "../../firebase/redirect";
import rightSide from "../../assets/newHome.jpg";

export default function Signup() {
  const [isSignup] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { trackSignUp, trackPageView } = useAnalytics();

  // Helper to get language from URL
  const getLangFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get("lang") === "zh" ? "zh" : "en";
  };

  const [language, setLanguage] = useState(getLangFromUrl());
  const isEN = language === "en";

  // Carried in from the login form, which got it from ProtectedRoute. Signup
  // itself ends on the "check your email" message rather than a redirect, so
  // this is really here to survive the trip back to /login afterwards.
  const from = readRedirect(location.search);

  // Keep language in sync with URL (if user pastes a new URL)
  useEffect(() => {
    // Track page view
    trackPageView("Signup Page");

    setLanguage(getLangFromUrl());
    // eslint-disable-next-line
  }, [location.search]);

  // Update URL when language changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("lang") !== language) {
      params.set("lang", language);
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line
  }, [language]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password || (isSignup && !name)) {
      setError(isEN ? "Please fill in all fields." : "請填寫所有欄位。");
      return;
    }

    // @oomii.com and @horuseyetech.com are internal: accepted here, but left
    // out of the message below and the Getting Started guide.
    if (
      !email.endsWith("@tglobalcorp.com") &&
      !email.endsWith("@oomii.com") &&
      !email.endsWith("@horuseyetech.com") &&
      !email.endsWith("@integrity-tech.com.tw") &&
      !email.endsWith("@xerendipity.ai")
    ) {
      setError(
        isEN
          ? "Please use a @tglobalcorp.com, @integrity-tech.com.tw, or @xerendipity.ai email address."
          : "請使用 @tglobalcorp.com、@integrity-tech.com.tw 或 @xerendipity.ai 電子郵件地址。",
      );
      return;
    }

    try {
      if (isSignup) {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password,
        );
        await updateProfile(userCredential.user, {
          displayName: name,
        });

        // Send verification email
        await sendEmailVerification(userCredential.user);

        // Track successful signup
        trackSignUp("email");

        // Show verification message instead of redirecting
        setShowVerificationMessage(true);
        setError(""); // Clear any previous errors
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        navigate(redirectTarget(location.search, language), {
          replace: true,
        });
      }
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="lg:flex h-screen font-sans relative">
      <div className="flex justify-center items-center lg:bg-slate-100 lg:w-1/2 relative z-10 min-h-screen lg:min-h-0">
        <div className="w-11/12 sm:w-3/4 lg:w-3/4 p-6 sm:p-8 bg-white/95 lg:bg-slate-100 backdrop-blur-sm lg:backdrop-blur-none rounded-xl lg:rounded-lg shadow-xl lg:shadow-none">
          {/* Language Switcher */}
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setLanguage("en")}
              className={`text-sm ml-2 px-2 py-1 rounded transition-all duration-200 ${
                language === "en"
                  ? "bg-orange-600 text-white"
                  : "text-gray-600 hover:bg-gray-200"
              }`}
            >
              English
            </button>
            <button
              onClick={() => setLanguage("zh")}
              className={`text-sm ml-2 px-2 py-1 rounded transition-all duration-200 ${
                language === "zh"
                  ? "bg-orange-600 text-white"
                  : "text-gray-600 hover:bg-gray-200"
              }`}
            >
              繁體中文
            </button>
          </div>

          {/* Logo */}
          <div
            onClick={() =>
              navigate({
                pathname: "/",
                search: `?lang=${language}`,
              })
            }
            className="flex items-center gap-6 mb-12"
          >
            <img
              src="logo_tglobal.png"
              alt="T-Global"
              className="w-48 h-auto"
            />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
            {isSignup && (
              <>
                <label className="text-gray-700 font-medium">
                  {isEN ? "Name" : "姓名"}
                </label>
                <input
                  type="text"
                  placeholder={isEN ? "Enter your name" : "請輸入姓名"}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </>
            )}

            <label className="text-gray-700 font-medium">
              {isEN ? "Email" : "電子郵件"}
            </label>
            <input
              type="email"
              placeholder={isEN ? "Enter your Email" : "請輸入商業電子郵件"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />

            <label className="text-gray-700 font-medium">
              {isEN ? "Password" : "密碼"}
            </label>
            <input
              type="password"
              placeholder={isEN ? "Enter your password" : "請輸入密碼"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />

            {error && <p className="text-red-600 text-sm">{error}</p>}

            {showVerificationMessage && (
              <div className="bg-green-50 border border-green-200 p-4 rounded-md">
                <div className="flex items-center">
                  <svg
                    className="w-6 h-6 text-green-600 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                  <div>
                    <h3 className="text-green-800 font-medium">
                      {isEN
                        ? "Account Created Successfully!"
                        : "帳號創建成功！"}
                    </h3>
                    <p className="text-green-700 text-sm mt-1">
                      {isEN
                        ? "We've sent a verification email to your address. Please check your email and click the verification link to complete your registration."
                        : "我們已向您的電子郵件發送驗證郵件。請檢查您的電子郵件並點擊驗證連結以完成註冊。"}
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate(`/verify-email?lang=${language}`)}
                      className="mt-3 text-green-600 hover:text-green-800 text-sm font-medium underline"
                    >
                      {isEN
                        ? "Continue to Email Verification"
                        : "繼續進行電子郵件驗證"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              className="p-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-md transition-colors duration-200"
            >
              {isSignup
                ? isEN
                  ? "Create account"
                  : "建立帳號"
                : isEN
                  ? "Login"
                  : "登入"}
            </button>
          </form>

          {/* Toggle Link */}
          <p className="mt-6 text-center text-gray-600">
            {isSignup
              ? isEN
                ? "Already have an account?"
                : "已經有帳號？"
              : isEN
                ? "New to T-Global?"
                : "還沒有帳號？"}{" "}
            <span
              className="text-blue-600 cursor-pointer underline hover:text-blue-800 transition-colors duration-200"
              onClick={() =>
                navigate({
                  pathname: "/login",
                  search: authSearch(language, from),
                })
              }
            >
              {isSignup
                ? isEN
                  ? "Login"
                  : "登入"
                : isEN
                  ? "Create account"
                  : "建立帳號"}
            </span>
          </p>

          {/* A new tab, so a half-filled form survives a look at the guide. */}
          <p className="mt-3 text-center text-sm text-gray-500">
            {isEN ? "Need help creating an account?" : "需要協助建立帳號？"}{" "}
            <a
              href={`/docs?lang=${language}&section=getting-started`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-800 transition-colors duration-200"
            >
              {isEN ? "View the Getting Started guide" : "查看開始使用指南"}
            </a>
          </p>
        </div>
      </div>

      {/* Right Panel */}
      <div className="lg:hidden absolute inset-0 z-0">
        <img src={rightSide} alt="" className="w-full h-full object-cover" />
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/40"></div>
      </div>

      {/* Right Panel */}
      <div className="lg:w-1/2 hidden lg:block flex-1 bg-slate-100 overflow-hidden relative p-8">
        <img
          src={rightSide}
          alt=""
          className="w-full h-full object-cover rounded-xl "
        />
      </div>
    </div>
  );
}
